import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldAlert } from "lucide-react";
import api, { extractApiError } from "@/lib/api";

/**
 * ReportUserDialog — small reusable dialog to report a driver or a hobby farmer.
 * Props: reportedUserId (string), reportedUserName (string), trigger? (custom trigger).
 */
export default function ReportUserDialog({ reportedUserId, reportedUserName, trigger, testidPrefix = "report" }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!reason.trim()) {
      toast.error("Please describe what happened.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/users/report", {
        reported_user_id: reportedUserId,
        reason: reason.trim(),
      });
      toast.success("Report submitted. Our admins review every report within 48 hours.");
      setReason("");
      setOpen(false);
    } catch (err) {
      toast.error(extractApiError(err, "Could not submit report."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button
            size="sm"
            variant="outline"
            data-testid={`${testidPrefix}-open`}
            className="border-amber-700/40 text-amber-800 hover:bg-amber-50"
          >
            <ShieldAlert size={14} className="mr-1.5" /> Report
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="bg-stone-50 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-stone-900">
            Report {reportedUserName || "user"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Label htmlFor="report-reason" className="text-sm font-semibold text-stone-800">
            What happened?
          </Label>
          <Textarea
            id="report-reason"
            data-testid={`${testidPrefix}-reason`}
            rows={5}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Briefly describe the issue. Mention dates, order IDs, or anything that helps us investigate."
            className="bg-white"
          />
          <p className="text-xs text-stone-500 font-sans">
            Your report is private. Admins review it within 48 hours.
          </p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} data-testid={`${testidPrefix}-cancel`}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            data-testid={`${testidPrefix}-submit`}
            className="bg-amber-700 hover:bg-amber-600 text-stone-50"
          >
            {submitting ? "Submitting…" : "Submit Report"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
